import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { isSeriesFullyLoaded } from '@/lib/viewer/seriesShape';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteImageThumbnail } from './DeleteImageThumbnail';
import { ImagePreviewPanel } from './ImagePreviewPanel';
/**
 * Grid of image thumbnails for selecting individual images to delete.
 * Features a split-view with hover preview panel on the left.
 * Shows all images from all series.
 */
export function DeleteImagesGrid({ allSeries, selectedImageIds, onSelectionChange, ensureSeriesInstances, }) {
    const { t } = useTranslation('viewer');
    const [hoveredImageUid, setHoveredImageUid] = useState(null);
    // Series whose instance rows haven't all paged in yet. Recomputed every
    // render so it tracks pages landing (a series drops out once its `images`
    // catches up to its summary count) — this both drives the ensure calls
    // below and gates the loading affordance.
    const pendingSeriesIds = useMemo(() => allSeries.filter((s) => !isSeriesFullyLoaded(s)).map((s) => s.id), [allSeries]);
    const isLoadingInstances = !!ensureSeriesInstances && pendingSeriesIds.length > 0;
    // Image-level delete genuinely needs the instance rows: a series the user
    // hasn't viewed yet opens this grid with `images: []` even though it may
    // hold thousands of instances (see lib/viewer/seriesShape.ts). Page those
    // series in via the viewer's existing ensure mechanism.
    useEffect(() => {
        if (!ensureSeriesInstances)
            return;
        pendingSeriesIds.forEach((seriesId) => {
            void ensureSeriesInstances(seriesId).catch((error) => {
                console.warn('[DeleteImagesGrid] failed to load series instances', error);
            });
        });
    }, [ensureSeriesInstances, pendingSeriesIds]);
    // Flatten all images from all series with series reference
    const allImages = useMemo(() => {
        return allSeries.flatMap((series) => series.images.map((img) => ({
            id: img.id,
            imageUid: img.imageUid,
            storageLocation: img.storageLocation,
            instanceNumber: img.instanceNumber,
            seriesNumber: series.seriesNumber,
            series,
        })));
    }, [allSeries]);
    const totalImages = allImages.length;
    const selectedCount = selectedImageIds.size;
    // Get the currently hovered image data
    const hoveredImageWithSeries = hoveredImageUid
        ? allImages.find((img) => img.imageUid === hoveredImageUid)
        : null;
    const handleToggleSelect = useCallback((imageUid) => {
        const newSelection = new Set(selectedImageIds);
        if (newSelection.has(imageUid)) {
            newSelection.delete(imageUid);
        }
        else {
            newSelection.add(imageUid);
        }
        onSelectionChange(newSelection);
    }, [selectedImageIds, onSelectionChange]);
    const handleSelectAll = useCallback(() => {
        const allImageUids = new Set(allImages.map((img) => img.imageUid));
        onSelectionChange(allImageUids);
    }, [allImages, onSelectionChange]);
    const handleDeselectAll = useCallback(() => {
        onSelectionChange(new Set());
    }, [onSelectionChange]);
    const handleHover = useCallback((imageUid) => {
        setHoveredImageUid(imageUid);
    }, []);
    return (_jsxs("div", { className: "py-2", children: [_jsxs("div", { className: "mb-3 flex flex-wrap items-center justify-between gap-2 px-1", children: [_jsx("div", { className: "text-muted-foreground text-sm", children: t('delete.seriesImagesTotal', {
                            series: allSeries.length,
                            images: totalImages,
                        }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm font-medium", children: t('delete.nSelected', { count: selectedCount }) }), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleSelectAll, disabled: selectedCount === totalImages, children: t('delete.selectAll') }), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleDeselectAll, disabled: selectedCount === 0, children: t('delete.deselectAll') })] })] }), isLoadingInstances && (_jsxs("div", { className: "border-border bg-muted/50 mb-3 flex items-center gap-2 rounded border p-2", "data-testid": "delete-images-loading", children: [_jsx(Loader2, { className: "text-muted-foreground h-4 w-4 shrink-0 animate-spin" }), _jsx("span", { className: "text-muted-foreground text-sm", children: t('viewport.loading') })] })), selectedCount === totalImages && totalImages > 0 && (_jsxs("div", { className: "mb-3 flex items-center gap-2 rounded border border-amber-500/20 bg-amber-500/10 p-2", children: [_jsx(AlertTriangle, { className: "h-4 w-4 shrink-0 text-amber-500" }), _jsx("span", { className: "text-sm text-amber-400", children: t('delete.allImagesWarning') })] })), _jsxs("div", { className: "flex gap-4", children: [_jsx(ImagePreviewPanel, { image: hoveredImageWithSeries
                            ? {
                                id: hoveredImageWithSeries.id,
                                imageUid: hoveredImageWithSeries.imageUid,
                                instanceNumber: hoveredImageWithSeries.instanceNumber,
                                simpleName: null,
                                storageLocation: hoveredImageWithSeries.storageLocation,
                            }
                            : null, series: hoveredImageWithSeries?.series ?? null, isVisible: !!hoveredImageWithSeries }), _jsx("div", { className: "grid max-h-[400px] min-w-0 flex-1 grid-cols-4 gap-2 overflow-y-auto p-1 sm:grid-cols-5 md:grid-cols-6", onMouseLeave: () => setHoveredImageUid(null), children: allImages.map((img) => (_jsx(DeleteImageThumbnail, { image: {
                                id: img.id,
                                imageUid: img.imageUid,
                                instanceNumber: img.instanceNumber,
                                simpleName: null,
                                storageLocation: img.storageLocation,
                            }, seriesNumber: img.seriesNumber, isSelected: selectedImageIds.has(img.imageUid), isHovered: hoveredImageUid === img.imageUid, onToggleSelect: handleToggleSelect, onHover: handleHover }, img.imageUid))) })] })] }));
}
